import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface SteamServerResponse {
  success: boolean;
  server: {
    id: string;
    name: string;
    ip_address: string;
    online_players: number;
    max_players: number;
    is_online: boolean;
    map?: string;
  };
  api_source: 'steam_api' | 'direct_query' | 'no_ip' | 'none';
  timestamp: string;
}

// Interface compatible avec les composants existants
interface SteamServerStatus {
  online: boolean;
  players?: number;
  maxPlayers?: number;
  map?: string;
  name?: string;
  game?: string;
}

export const useSteamServerStatus = (serverId: string, enabled: boolean | string = true) => {
  // Convertir string en boolean si nécessaire
  const isEnabled = typeof enabled === 'string' ? true : enabled;
  
  return useQuery({
    queryKey: ['steam-server-status', serverId],
    queryFn: async (): Promise<SteamServerStatus> => {
     // console.log('🔍 Fetching REAL Steam data for server:', serverId);
      
      const { data, error } = await supabase.functions.invoke('steam-server-status', {
        body: { serverId },
      });

      if (error) {
       // console.error('❌ Steam API error:', error);
        throw new Error(error.message || 'Failed to fetch Steam server status');
      }

     // console.log('✅ Steam API response received:', data);
      
      // Adapter la réponse pour maintenir la compatibilité
      const adapted: SteamServerStatus = {
        online: data.server?.is_online || false,
        players: data.server?.online_players,
        maxPlayers: data.server?.max_players,
        map: data.server?.map,
        name: data.server?.name,
        game: 'Steam Game'
      };
      
      return adapted;
    },
    enabled: isEnabled && !!serverId,
    staleTime: 5 * 60 * 1000, // 5 minutes - données Steam assez stables
    refetchInterval: 10 * 60 * 1000, // Refetch every 10 minutes
    refetchOnWindowFocus: false, // Éviter trop de requêtes à l'API Steam
    retry: 2, // Retry max 2 fois en cas d'erreur
  });
};

// Hook pour mettre à jour plusieurs serveurs en batch
export const useBatchSteamUpdate = () => {
  const updateServerStats = async (serverIds: string[]) => {
    //console.log('🔄 Batch updating Steam data for servers:', serverIds.length);
    
    const results = await Promise.allSettled(
      serverIds.map(async (serverId) => {
        const { data } = await supabase.functions.invoke('steam-server-status', {
          body: { serverId },
        });
        return data;
      })
    );

    const successful = results
      .filter((result): result is PromiseFulfilledResult<SteamServerResponse> => 
        result.status === 'fulfilled'
      )
      .map(result => result.value);

    //console.log(`✅ Successfully updated ${successful.length}/${serverIds.length} servers with Steam data`);
    
    return successful;
  };

  return { updateServerStats };
};